import { createDrawerTabController } from './component-search-shared.js';

const ROOT_SELECTOR = '[data-featured-collection-tabs]';
const TAB_TRIGGER_SELECTOR = '[data-drawer-menu-tab]';

const controllers = new Map();

class FeaturedCollectionTabsController {
  constructor(root) {
    this.root = root;
    this.defaultTab = '';
    this.tabController = null;
    this.refreshRaf = 0;
    this.refreshTimeout = 0;
    this.lastActivePanel = null;
    this.leaveTimer = 0;

    this.onTabInteraction = this.onTabInteraction.bind(this);
  }

  init() {
    this.defaultTab = (this.root.dataset.defaultTab || '').trim();
    this.tabController = createDrawerTabController(this.root, {
      defaultTab: this.defaultTab
    });

    this.lastActivePanel = this.root.querySelector('.featured-collection__tab-panel.is-active');

    this.root.addEventListener('click', this.onTabInteraction);
    this.root.addEventListener('keydown', this.onTabInteraction);

    this.queueRefresh();
    return true;
  }

  destroy() {
    this.root.removeEventListener('click', this.onTabInteraction);
    this.root.removeEventListener('keydown', this.onTabInteraction);

    if (this.tabController) {
      this.tabController.destroy();
      this.tabController = null;
    }

    if (this.refreshRaf) {
      window.cancelAnimationFrame(this.refreshRaf);
      this.refreshRaf = 0;
    }

    if (this.refreshTimeout) {
      window.clearTimeout(this.refreshTimeout);
      this.refreshTimeout = 0;
    }

    if (this.leaveTimer) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = 0;
    }

    const leaving = this.root.querySelector('.featured-collection__tab-panel.is-panel-leaving');
    if (leaving) {
      leaving.classList.remove('is-panel-leaving');
      leaving.hidden = true;
    }
  }

  onTabInteraction(event) {
    if (!(event.target instanceof Element)) return;

    const tabTrigger = event.target.closest(TAB_TRIGGER_SELECTOR);
    if (!tabTrigger || !this.root.contains(tabTrigger)) return;

    // The shared tab controller has already switched tabs by now.
    // Crossfade: briefly un-hide the previous panel so it can fade out.
    const newActivePanel = this.root.querySelector('.featured-collection__tab-panel.is-active');
    const leavingPanel = this.lastActivePanel;

    if (leavingPanel && newActivePanel && leavingPanel !== newActivePanel) {
      this.crossfadeLeaving(leavingPanel);
    }

    this.lastActivePanel = newActivePanel;
    this.queueRefresh();
  }

  crossfadeLeaving(panel) {
    // Clean up any in-progress leave transition
    if (this.leaveTimer) {
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = 0;
      const prev = this.root.querySelector('.featured-collection__tab-panel.is-panel-leaving');
      if (prev) {
        prev.classList.remove('is-panel-leaving');
        prev.hidden = true;
      }
    }

    // Skip crossfade for reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Un-hide the leaving panel and start the fade-out
    panel.hidden = false;
    void panel.offsetHeight;
    panel.classList.add('is-panel-leaving');

    this.leaveTimer = window.setTimeout(() => {
      panel.classList.remove('is-panel-leaving');
      panel.hidden = true;
      this.leaveTimer = 0;
    }, 350);
  }

  queueRefresh() {
    if (this.refreshRaf) {
      window.cancelAnimationFrame(this.refreshRaf);
    }

    this.refreshRaf = window.requestAnimationFrame(() => {
      this.refreshRaf = 0;
      this.refreshActivePanelSlider();
    });

    if (this.refreshTimeout) {
      window.clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = window.setTimeout(() => {
      this.refreshTimeout = 0;
      this.refreshActivePanelSlider();
    }, 80);
  }

  refreshActivePanelSlider() {
    const activePanel = this.root.querySelector('[data-drawer-menu-tab-panel].is-active:not([hidden])');
    if (!(activePanel instanceof HTMLElement)) return;

    const slider = activePanel.querySelector('grid-slider');
    if (!(slider instanceof HTMLElement)) return;

    const swiperInstance = slider.swiper;
    if (swiperInstance && typeof swiperInstance.update === 'function') {
      swiperInstance.update();
    }
  }
}

const initRoot = (root) => {
  if (!root || controllers.has(root)) return;

  const controller = new FeaturedCollectionTabsController(root);
  if (controller.init()) {
    controllers.set(root, controller);
  }
};

const destroyRoot = (root) => {
  const controller = controllers.get(root);
  if (!controller) return;
  controller.destroy();
  controllers.delete(root);
};

const initAll = (scope = document) => {
  scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
};

const destroyInSection = (sectionRoot) => {
  if (!sectionRoot) return;
  sectionRoot.querySelectorAll(ROOT_SELECTOR).forEach(destroyRoot);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionRoot = event.target.closest('.shopify-section') || event.target;
  if (!sectionRoot) return;

  destroyInSection(sectionRoot);
  initAll(sectionRoot);
});

document.addEventListener('shopify:section:unload', (event) => {
  const sectionRoot = event.target.closest('.shopify-section') || event.target;
  destroyInSection(sectionRoot);
});
